import React, { useState } from 'react'
import { number, oneOfType, shape, string } from 'prop-types'
import { css } from '@emotion/core'
import { useAtSize } from '@fs/zion-ui'
import HeadingWithIcon from './TileContent/HeadingWithIcon'
import QuizComparison from './TileContent/Quiz/QuizComparison'
import EventComparison from './TileContent/Event/EventComparison'
import LineChartComparison from './TileContent/LineChart/LineChartComparison'
import NameMeaningComparison from './TileContent/NameMeaning/NameMeaningComparison'
import GlobalNameCountsComparison from './TileContent/GlobalNameCounts/GlobalNameCountsComparison'
import NameHeatmapComparison from './TileContent/NameHeatmap/NameHeatmapComparison'
import TempleDedications from './TileContent/TempleDedications/TempleDedications'
import { useTileContentData, useInnerTileVisuals } from '../hooks/hooks'
import { deconstructName, formatName } from '../../../shared/helpers/name_formatting'
import AncestorSlider from './AncestorSlider/AncestorSlider'
import PageLoadingIndicator from '../../../shared/components/PageLoadingIndicator'
import AddTreeFlow from '../../../shared/components/AddTreeFlow'
import { useAamState, useAamDispatch } from '../state/AamStore'
import { formatTileContentData } from '../services/services'

export default function TileContent({ tileData, highlightColor }) {
  const [isATFShowing, setIsATFShowing] = useState(false)
  const [tileContentData, tileContentDispatch] = useTileContentData({ tileData })
  const [tileComparisonContent, tileComparisonContentDispatch, gettileComparisonContent] = useTileContentData({
    tileData,
    autoLoad: false,
  })
  const [tileContentProps, setTileContentProps] = useState({ ...tileData })
  const atSize = useAtSize()
  const components = {
    quiz: QuizComparison,
    event: EventComparison,
    lineChart: LineChartComparison,
    nameMeaning: NameMeaningComparison,
    globalNameCounts: GlobalNameCountsComparison,
    nameHeatmap: NameHeatmapComparison,
    templeDedications: TempleDedications,
  }
  const SpecificTileContent = components[tileData.type]
  const tileVisuals = useInnerTileVisuals(tileContentProps.imageUrl)
  const aamState = useAamState()
  const aamDispatch = useAamDispatch()

  const updateTileContent = (event, relation) => {
    const fullName = relation.displayName
    const formattedName = formatName(fullName)
    const comparisonParsedNames = deconstructName(formattedName)
    const { dob } = relation
    const { region, id } = tileData
    const { name, isFirst } = formatTileContentData({ ...tileData, parsedNames: comparisonParsedNames }).nameArgs
    const tileProps = {
      ...tileData,
      comparisonParsedNames,
      countryCode: region,
      comparisonYear: dob ? parseInt(dob, 10) : 0,
      tileId: id,
      comparisonTargetPerson: relation,
      comparisonTargetNameArgs: { name, isFirst },
    }

    const { countryCode, comparisonYear, tileId, comparisonTargetNameArgs } = tileProps

    gettileComparisonContent({
      countryCode,
      year: comparisonYear,
      tileId,
      nameArgs: comparisonTargetNameArgs,
    })

    setTileContentProps(tileProps)
  }

  const toggleAtf = () => {
    setIsATFShowing(!isATFShowing)
  }

  const tileContentStyle = css`
    height: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    background-image: url(${tileVisuals.background});
    background-size: cover;
    background-position: center;
    overflow: hidden;
  `
  const innerTileContentStyle = css`
    align-items: center;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;
    width: 100%;
    padding-top: ${atSize({ default: '10px', lg: '20px' })};
  `

  return !isATFShowing ? (
    <div css={tileContentStyle}>
      <AncestorSlider updateTileContent={updateTileContent} tileData={tileData} toggleAtf={toggleAtf} />
      <div css={innerTileContentStyle}>
        {tileContentData.load || tileComparisonContent.load ? (
          <PageLoadingIndicator constrain />
        ) : (
          <SpecificTileContent
            highlightColor={highlightColor}
            tileData={tileData}
            tileContentData={tileContentData?.res ?? null}
            tileContentDispatch={tileContentDispatch}
            tileComparisonContent={tileComparisonContent?.res ?? null}
            tileComparisonContentDispatch={tileComparisonContentDispatch}
            headingWithIcon={
              <HeadingWithIcon
                highlightColor={highlightColor}
                headerIcon={tileVisuals.icon}
                txt={tileContentData?.res?.[0]?.textTitle ?? tileData.header}
              />
            }
            {...tileContentProps}
          />
        )}
      </div>
    </div>
  ) : (
    <AddTreeFlow
      toggleAtf={toggleAtf}
      sourceThatInitiated="All About Me"
      aamState={aamState}
      aamDispatch={aamDispatch}
    />
  )
}

TileContent.propTypes = {
  tileData: shape({
    additionalText: string,
    header: string,
    icon: string,
    id: number,
    img: string,
    language: string,
    parsedNames: shape({}),
    region: string,
    title: string,
    type: string,
    year: oneOfType([string, number]),
  }),
  highlightColor: string,
}
